import React from 'react';
import { navigateToLanding } from '../services/router';

/**
 * Error Page
 * 
 * Centralized error handling and recovery interface.
 * Handles:
 * - Navigation errors (unknown routes, missing group state)
 * - Link/Import errors (malformed payloads, expired links)
 * - Storage errors (quota exceeded, blocked storage)
 * - Validation errors (computation failures, invariant violations)
 * - Network/connectivity issues
 * 
 * Features:
 * - Clear error descriptions with recovery actions
 * - "Go Home" navigation
 * - Error reporting (dev mode)
 * - Alternative import/export flows
 * 
 * User Journey: Fallback when things go wrong, with clear recovery paths
 */

interface ErrorPageProps {
  errorCode?: string;
}

const Error: React.FC<ErrorPageProps> = ({ errorCode }) => {
  const getErrorInfo = (code?: string) => {
    switch (code) {
      case 'invalid-route':
        return {
          title: 'Invalid Route',
          message: 'The URL you\'re trying to access is not valid.',
          suggestion: 'Please check the link and try again, or go back to the homepage.'
        };
      case 'group-not-found':
        return {
          title: 'Group Not Found',
          message: 'The group you\'re looking for doesn\'t exist or has been deleted.',
          suggestion: 'The link might be expired or incorrect. Try creating a new group.'
        };
      case 'storage-error':
        return {
          title: 'Storage Error',
          message: 'Unable to save or load data from your browser.',
          suggestion: 'Your browser storage might be full. Try clearing some data or using a different browser.'
        };
      default:
        return {
          title: 'Something went wrong',
          message: 'An unexpected error occurred.',
          suggestion: 'Please try refreshing the page or go back to the homepage.'
        };
    }
  };

  const errorInfo = getErrorInfo(errorCode);

  const handleGoHome = () => {
    navigateToLanding();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="error-page content" style={{ maxWidth: '600px', margin: '0 auto', marginTop: '60px', textAlign: 'center' }}>
      <h1 style={{ color: 'var(--color-error)', marginBottom: '16px' }}>
        {errorInfo.title}
      </h1>
      
      <p style={{ fontSize: '16px', marginBottom: '8px', color: 'var(--color-gray-600)' }}>
        {errorInfo.message}
      </p>
      
      <p style={{ fontSize: '14px', marginBottom: '32px', color: 'var(--color-gray-600)' }}>
        {errorInfo.suggestion}
      </p>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button className="btn btn-primary" onClick={handleGoHome}>
          Go to Homepage
        </button>
        <button className="btn btn-secondary" onClick={handleRefresh}>
          Refresh Page
        </button>
      </div>

      {errorCode && (
        <div style={{ 
          marginTop: '40px', 
          padding: '16px', 
          backgroundColor: 'var(--color-gray-50)', 
          borderRadius: 'var(--radius-lg)',
          fontSize: '12px',
          color: 'var(--color-gray-600)'
        }}>
          <p><strong>Error Code:</strong> {errorCode}</p>
          <p><strong>Time:</strong> {new Date().toLocaleString()}</p>
        </div>
      )}
    </div>
  );
};

export default Error;
